import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../styles/contatti.css';

const CONTACT_API_URL =
	process.env.REACT_APP_CONTACT_API_URL || 'https://send.ingegneriabernasconi.it/api/contact';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const serviceLabels = {
	'ai-ml': 'AI & Machine Learning',
	'web-dev': 'Sviluppo Web',
	'consulting': 'Consulenza IT',
	'data-eng': 'Data & BI',
	'cybersec': 'Cybersecurity',
	'cloud-auto': 'Cloud & Automazione',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^[+\d\s\-().]{0,20}$/;
const RATE_LIMIT_MS = 30000;

function sanitizeInput(value) {
	return value
		.replace(/<[^>]*>/g, '')
		.replace(/javascript:/gi, '')
		.replace(/on\w+\s*=/gi, '')
		.replace(/data:\s*[^,]*,/gi, '')
		.trim();
}

export default function ContactUs({ selectedService }) {
	const { t } = useTranslation();
	const [from_name, setFrom_name] = useState('');
	const [from_surname, setFrom_surname] = useState('');
	const [from_mail, setFrom_mail] = useState('');
	const [from_phone, setFrom_phone] = useState('');
	const [message, setMessage] = useState('');
	const [privacy, setPrivacy] = useState(false);
	const [status, setStatus] = useState(null);
	const [sending, setSending] = useState(false);
	const [honeypot, setHoneypot] = useState('');
	const [captchaToken, setCaptchaToken] = useState('');
	const lastSubmitRef = useRef(0);
	const statusRef = useRef(null);
	const widgetRef = useRef(null);
	const widgetIdRef = useRef(null);

	const showStatus = useCallback((value) => {
		setStatus(value);
		setTimeout(() => setStatus(null), 6000);
	}, []);

	useEffect(() => {
		if (status && statusRef.current) {
			statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [status]);

	// Carica e renderizza il widget Cloudflare Turnstile
	useEffect(() => {
		if (!TURNSTILE_SITE_KEY) return undefined;

		let timer = null;
		const tryRender = () => {
			if (window.turnstile && widgetRef.current && widgetIdRef.current === null) {
				widgetIdRef.current = window.turnstile.render(widgetRef.current, {
					sitekey: TURNSTILE_SITE_KEY,
					callback: (token) => setCaptchaToken(token),
					'expired-callback': () => setCaptchaToken(''),
					'error-callback': () => setCaptchaToken(''),
				});
			}
			if (widgetIdRef.current !== null && timer) {
				clearInterval(timer);
				timer = null;
			}
		};

		if (!document.getElementById('cf-turnstile-script')) {
			const s = document.createElement('script');
			s.id = 'cf-turnstile-script';
			s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
			s.async = true;
			s.defer = true;
			document.head.appendChild(s);
		}

		timer = setInterval(tryRender, 200);
		tryRender();

		return () => {
			if (timer) clearInterval(timer);
		};
	}, []);

	async function sendEmail(e) {
		e.preventDefault();

		if (honeypot) return;

		const now = Date.now();
		if (now - lastSubmitRef.current < RATE_LIMIT_MS) {
			showStatus('rate-limit');
			return;
		}

		const cleanName = sanitizeInput(from_name);
		const cleanSurname = sanitizeInput(from_surname);
		const cleanMail = sanitizeInput(from_mail);
		const cleanPhone = sanitizeInput(from_phone);
		const cleanMessage = sanitizeInput(message);

		if (!cleanName || !cleanSurname || !cleanMail || !cleanMessage) {
			showStatus('missing');
			return;
		}

		if (!EMAIL_REGEX.test(cleanMail)) {
			showStatus('invalid-email');
			return;
		}

		if (cleanPhone && !PHONE_REGEX.test(cleanPhone)) {
			showStatus('invalid-phone');
			return;
		}

		if (!privacy) {
			showStatus('privacy');
			return;
		}

		if (TURNSTILE_SITE_KEY && !captchaToken) {
			showStatus('captcha');
			return;
		}

		const serviceName = selectedService ? serviceLabels[selectedService] || selectedService : t('form.notSpecified');

		setSending(true);
		lastSubmitRef.current = Date.now();

		try {
			const res = await fetch(CONTACT_API_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					from_name: cleanName,
					from_surname: cleanSurname,
					from_mail: cleanMail,
					from_phone: cleanPhone,
					service: serviceName,
					message: cleanMessage,
					turnstile_token: captchaToken,
					contact_extra_field: honeypot,
				}),
			});
			const data = await res.json().catch(() => ({}));

			if (res.ok && data.ok) {
				showStatus('success');
				setFrom_name('');
				setFrom_surname('');
				setFrom_mail('');
				setFrom_phone('');
				setMessage('');
				setPrivacy(false);
				e.target.reset();
			} else if (res.status === 429 || data.error === 'rate-limit') {
				showStatus('rate-limit');
			} else if (data.error === 'captcha') {
				showStatus('captcha');
			} else {
				showStatus('error');
			}
		} catch (err) {
			showStatus('error');
		} finally {
			setSending(false);
			setCaptchaToken('');
			if (window.turnstile && widgetIdRef.current !== null) {
				try {
					window.turnstile.reset(widgetIdRef.current);
				} catch (_) {
					/* noop */
				}
			}
		}
	}

	return (
		<form onSubmit={sendEmail} className="contact-form">
			<input
				type="text"
				name="contact_extra_field"
				value={honeypot}
				onChange={(e) => setHoneypot(e.target.value)}
				style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
				tabIndex={-1}
				autoComplete="off"
				data-lpignore="true"
				data-1p-ignore="true"
				data-form-type="other"
				aria-hidden="true"
			/>

			<div className="form-row">
				<div className="form-group">
					<label htmlFor="from_name" className="form-label">{t('form.firstName')} *</label>
					<input
						type="text"
						id="from_name"
						name="from_name"
						className="form-input"
						placeholder={t('form.firstNamePlaceholder')}
						maxLength={100}
						value={from_name}
						onChange={(e) => setFrom_name(e.target.value)}
						required
					/>
				</div>
				<div className="form-group">
					<label htmlFor="from_surname" className="form-label">{t('form.lastName')} *</label>
					<input
						type="text"
						id="from_surname"
						name="from_surname"
						className="form-input"
						placeholder={t('form.lastNamePlaceholder')}
						maxLength={100}
						value={from_surname}
						onChange={(e) => setFrom_surname(e.target.value)}
						required
					/>
				</div>
			</div>

			<div className="form-row">
				<div className="form-group">
					<label htmlFor="from_mail" className="form-label">{t('form.email')} *</label>
					<input
						type="email"
						id="from_mail"
						name="from_mail"
						className="form-input"
						placeholder={t('form.emailPlaceholder')}
						maxLength={254}
						value={from_mail}
						onChange={(e) => setFrom_mail(e.target.value)}
						required
					/>
				</div>
				<div className="form-group">
					<label htmlFor="from_phone" className="form-label">{t('form.phone')}</label>
					<input
						type="tel"
						id="from_phone"
						name="from_phone"
						className="form-input"
						placeholder={t('form.phonePlaceholder')}
						maxLength={20}
						value={from_phone}
						onChange={(e) => setFrom_phone(e.target.value)}
					/>
				</div>
			</div>

			<div className="form-group">
				<label htmlFor="message" className="form-label">{t('form.message')} *</label>
				<textarea
					id="message"
					name="message"
					className="form-input form-textarea"
					placeholder={t('form.messagePlaceholder')}
					maxLength={5000}
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					required
				/>
			</div>

			<div className="form-privacy">
				<label className="privacy-label">
					<input
						type="checkbox"
						checked={privacy}
						onChange={(e) => setPrivacy(e.target.checked)}
						className="privacy-checkbox"
					/>
					<span className="privacy-text">
						{t('form.privacyConsent')}{' '}
						<a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
							{t('form.privacyPolicy')}
						</a>{' '}
						{t('form.privacyRef')} *
					</span>
				</label>
			</div>

			{TURNSTILE_SITE_KEY && (
				<div className="form-turnstile" ref={widgetRef} style={{ marginBottom: 12 }} />
			)}

			<div ref={statusRef}>
				{status === 'success' && (
					<div className="form-status form-success">
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, verticalAlign: 'middle' }}>
							<path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
							<polyline points="22 4 12 14.01 9 11.01" />
						</svg>
						{t('form.success')}
					</div>
				)}
				{status === 'error' && (
					<div className="form-status form-error">{t('form.error')}</div>
				)}
				{status === 'missing' && (
					<div className="form-status form-warning">{t('form.missing')}</div>
				)}
				{status === 'privacy' && (
					<div className="form-status form-warning">{t('form.privacy')}</div>
				)}
				{status === 'invalid-email' && (
					<div className="form-status form-warning">{t('form.invalidEmail')}</div>
				)}
				{status === 'invalid-phone' && (
					<div className="form-status form-warning">{t('form.invalidPhone')}</div>
				)}
				{status === 'rate-limit' && (
					<div className="form-status form-warning">{t('form.rateLimit')}</div>
				)}
				{status === 'captcha' && (
					<div className="form-status form-warning">{t('form.captcha')}</div>
				)}
			</div>

			<button
				className="contact-submit-btn"
				type="submit"
				disabled={sending}
			>
				{sending ? (
					<>
						<span className="btn-spinner"></span>
						{t('form.sending')}
					</>
				) : (
					<>
						{t('form.submit')}
						<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 8 }}>
							<line x1="5" y1="12" x2="19" y2="12" />
							<polyline points="12 5 19 12 12 19" />
						</svg>
					</>
				)}
			</button>
		</form>
	);
}
